import { z } from 'zod';

/**
 * The last thing the progress poller saw, as it is stored on the study and rendered.
 *
 * A snapshot of an observation rather than a running total, which is what makes the two
 * awkward properties of this number safe to hold. It is written in snake_case because it is
 * a jsonb column parsed on read, like every other stored payload here.
 */
export const storedStudyProgressSchema = z.object({
  /**
   * N, from Remesh's own record of it rather than from the artifact — the study is being
   * fielded against the number Remesh has, and a re-read artifact could have moved.
   *
   * Null before the audience is applied, which the poller never sees, so it is nullable for
   * the same reason the port's field is: there is nothing honest to put there.
   */
  target_completes: z.int().nullable(),
  /**
   * How many participants have completed.
   *
   * **Null means _not applicable_, never none.** The count exists only for flex studies, and
   * rendering a null as zero is a claim that nobody has answered a study that never had the
   * concept — the one reading of this field that is always wrong.
   *
   * It can also **go down**. A returned Prolific submission un-completes a participant, so a
   * later poll legitimately reports fewer than an earlier one. Nothing here clamps it or
   * treats a decrease as corruption: a progress card that refused to move backwards would be
   * showing a number Remesh has stopped agreeing with.
   */
  completed_count: z.int().nullable(),
  /** When this observation was taken, which is what makes a stale card visibly stale. */
  read_at: z.iso.datetime(),
});
export type StoredStudyProgress = z.infer<typeof storedStudyProgressSchema>;

/**
 * The share of N that has completed, as a fraction, or null where there is no share to
 * compute — no target, no applicable count, or a target of zero.
 *
 * Not clamped, deliberately. Over-recruitment is real (Remesh admits N+5 to absorb dropouts),
 * so a fraction above one is an observation rather than an error, and a caller that wants a
 * bar to stop at the end can say so where it draws the bar. Under-reporting a study that
 * actually over-delivered would be the more misleading of the two.
 */
export const studyProgressFraction = (progress: StoredStudyProgress): number | null => {
  const { target_completes: target, completed_count: completed } = progress;

  if (target === null || target <= 0 || completed === null) {
    return null;
  }

  return completed / target;
};
